import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FmBlackPopup, FmIcon, FmTextField, FmUnderlineNav } from "../../Components";
import { useIsMobile, useTheme } from "../../Hooks";
import Tag from "./Tag/Tag";
import SavedItem from "./SavedItem/SavedItem";
import { useDispatch, useSelector } from "react-redux";
import { SavedItemsState, AgriSolutionState, GeneralState, SolutionState } from "../../Redux/States";
import { TimeUtils } from "../../Utils";
import { SavedItemModel } from "../../Models/ServerModels/SavedItems/SavedItem";

import useStyles from "./SavedItemsPage.styles";
import { ZERO } from "../../Utils/Consts";

let initSavedItems: SavedItemModel[] = [];

const SavedItemsPage = () => {
  const [tagList, setTagList] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<any>("");
  const [filteredSaveItems, setFilteredSaveItems] = useState<SavedItemModel[]>(initSavedItems);
  const [itemsToDelete, setItemsToDelete] = useState<SavedItemModel[]>([]);
  const [itemIdToDelete, setItemIdToDelete] = useState<number | null>(null);
  const [loadAll, setLoadAll] = useState<boolean>(false);
  const [showBlackPopup, setShowBlackPopup] = useState<boolean>(false);

  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  useEffect(() => {
    const getAllSavedItemHandler = async () => {
      await dispatch(SavedItemsState.Thunks.getAllSavedItems());
    };

    getAllSavedItemHandler();
  }, []);

  initSavedItems = useSelector(SavedItemsState.Selectors.selectSavedItems);

  useEffect(() => {
    (window as any).dataLayer.push({
      event: "SavedItemsPage",
      pageUrl: "https://multimatch.haifa-group.com/multi-match/saved-items",
      pageTitle: "SavedItemsPage",
    });
  }, []);

  const handleFiltered = useCallback(() => {
    const filterSavedItemArray =
      tagList.length > 0
        ? initSavedItems.filter((item: SavedItemModel) => {
            const countryName = t(`countries.${item.countryCode}`);

            const itemAreaLowerCase = countryName.toLowerCase().trim();
            const itemTitleLowerCase = item.name.toLowerCase().trim();
            const notFoundTag = tagList.find((tag: string) => !itemAreaLowerCase.includes(tag) && !itemTitleLowerCase.includes(tag));

            return !notFoundTag;
          })
        : initSavedItems;
    setFilteredSaveItems(filterSavedItemArray);
  }, [initSavedItems, tagList]);

  useEffect(() => {
    handleFiltered();
  }, [handleFiltered]);

  const reset = () => {
    //reset green line
    dispatch(
      GeneralState.Actions.setGreenBar({
        isShow: false,
        lastIsShow: true,
      }),
    );
    //dispatch(GeneralState.Actions.setShowEditNavbar(false));
    dispatch(AgriSolutionState.Actions.setItemId(null));
    dispatch(SolutionState.Actions.setItemId(null));
    //reset summary
    dispatch(GeneralState.Actions.setIsAgri(false));
  };
  useEffect(() => {
    reset();
  }, []);

  const handleOnEnter = (e: any) => {
    if (e.keyCode === 13) {
      if (tagList && !tagList.includes(e.target.value.toLowerCase().trim())) {
        const newArr = [...tagList, e.target.value.toLowerCase().trim()];
        setTagList(newArr);
        setSearchValue("");
      }
    }
  };

  const handleDeleteTag = (tag: string) => {
    const tagLowerCase = (tag || "").toLowerCase().trim();
    const newArray = tagList.filter((_tag: string) => tagLowerCase !== _tag);

    setTagList(newArray);
  };

  const handleLoadAll = () => {
    setLoadAll((prev) => !prev);
  };

  const handleClearAll = () => {
    setTagList([]);
  };

  const numberOfItemToLoad = isMobile ? 10000 : 10000;
  const numberOfTagsToLoad = 3;

  const onDuplicate = async (itemId: number) => {
    await dispatch(SavedItemsState.Thunks.duplicateSaveItemById(itemId));
  };

  const onDelete = async (itemId: number, e: any) => {
    setItemIdToDelete(itemId);
    setShowBlackPopup(true);
    const item = initSavedItems.find((i) => i.id == itemId);
    if (item) setItemsToDelete([item]);
    dispatch(SavedItemsState.Actions.setSavedItems(initSavedItems.filter((i) => i.id !== itemId)));
  };

  const onRestoreItemHandler = async () => {
    const id = itemIdToDelete;
    await setItemIdToDelete(null);
    const item = itemsToDelete.find((i) => i.id == id);
    if (item) {
      dispatch(SavedItemsState.Actions.setSavedItems([...initSavedItems, item]));
      const index = itemsToDelete.findIndex((i) => i.id == id);
      itemsToDelete.splice(index, 1);
      setItemsToDelete(itemsToDelete);
    }
  };

  const closePopupHandler = async () => {
    const lastItem = itemsToDelete.pop();
    lastItem && (await dispatch(SavedItemsState.Thunks.deleteSaveItemById(lastItem.id)));

    setShowBlackPopup(false);
  };

  const onClickClosePopupHandler = async () => {
    setShowBlackPopup(false);
  };

  const onRename = async (itemId: number, newName: string) => {
    await dispatch(SavedItemsState.Thunks.renameSaveItemById(itemId, newName));
  };

  return (
    <>
      {showBlackPopup && (
        <div className={isMobile ? classes.mobile_blackPopup_container : classes.blackPopup_container}>
          <FmBlackPopup
            text="Item deleted"
            onActionText="Restore"
            onClose={closePopupHandler}
            onAction={onRestoreItemHandler}
            onCloseWithPress={onClickClosePopupHandler}
            snackbar={true}
          />
        </div>
      )}
      <div className={isMobile ? classes.mobile_container : classes.container}>
        <span className={isMobile ? classes.mobile_pageTitle : classes.pageTitle}>{t("saveItemsPage.title")}</span>
        <div className={classes.inputSearch_container}>
          <FmTextField
            variant="filled"
            placeholder={isMobile ? t("saveItemsPage.mobile_inputPlaceholder") : t("saveItemsPage.inputPlaceholder")}
            fullWidth
            onKeyUp={(e: any) => handleOnEnter(e)}
            onChange={(e: any) => setSearchValue(e.target.value)}
            displayIcon={true}
            inputProps={{
              icon: <FmIcon url={theme.images.search} title={"visible"} />,
            }}
            value={searchValue}
          />
        </div>
        <div className={classes.tagsAndClear_container}>
          <div className={classes.tags_container}>
            {tagList.map((tag: string, index: any) => (
              <Tag key={index} onClose={() => handleDeleteTag(tag)}>
                {tag}
              </Tag>
            ))}
          </div>
          {tagList.length >= numberOfTagsToLoad && (
            <div className={classes.clearAll_container} onClick={handleClearAll}>
              <FmUnderlineNav title={t("saveItemsPage.clearAll")} />
            </div>
          )}
        </div>

        <div className={classes.items_container}>
          {filteredSaveItems
            ? filteredSaveItems.map((item: any, index: number) => {
                if (index < numberOfItemToLoad) {
                  const countryName = item.countryCode ? t(`countries.${item.countryCode}`) : "";
                  const dateToDisplay = TimeUtils.getDateFormattedToSavedItem(item.createdAt);

                  return (
                    <SavedItem
                      key={item.id}
                      id={item.id}
                      title={item.name}
                      date={dateToDisplay}
                      area={countryName}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onRename={onRename}
                    />
                  );
                }
              })
            : initSavedItems.map((item, index) => {
                if (index < numberOfItemToLoad) {
                  const countryName = t(`countries.${item.countryCode}`);
                  const dateToDisplay = TimeUtils.getDateFormattedToSavedItem(item.createdAt);

                  return (
                    <SavedItem
                      key={item.id}
                      id={item.id}
                      title={item.name}
                      date={dateToDisplay}
                      area={countryName}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onRename={onRename}
                    />
                  );
                }
              })}
        </div>
        {!loadAll && initSavedItems.length > numberOfItemToLoad && (
          <div className={classes.loadMore_container} onClick={handleLoadAll}>
            <FmUnderlineNav title={t("saveItemsPage.loadMore")} />
          </div>
        )}
      </div>
    </>
  );
};

export default SavedItemsPage;
